import { type CalendarDate, parseDate } from "@internationalized/date"
import type { ClassValue } from "clsx"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function formatAmount(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n)
}

export function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

export function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0])
}

export function timeAgo(dateStr: string) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60)      return 'just now'
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`
  return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Calendar date helpers ─────────────────────────────────────────────────
// Wire format between the frontend forms and the Rails backend is always
// YYYY-MM-DD (a bare date string). These helpers convert between that and
// the CalendarDate objects that reka-ui's date picker primitives expect.

/** Parse a YYYY-MM-DD string into a reka-ui CalendarDate. Returns undefined for empty/invalid. */
export function toCalendarDate(s: string | null | undefined): CalendarDate | undefined {
  if (!s) return undefined
  try { return parseDate(s) } catch { return undefined }
}

/** Serialise a CalendarDate back to a YYYY-MM-DD string for the backend. */
export function fromCalendarDate(d: CalendarDate): string {
  return d.toString() // CalendarDate#toString() always returns YYYY-MM-DD
}

/** Format a CalendarDate for display in Australian locale (e.g. "3 Jan 2025"). */
export function displayCalendarDate(d: CalendarDate): string {
  return new Date(d.year, d.month - 1, d.day)
    .toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}